
class UniversalProducts extends HTMLElement {
  constructor() {
    super();
    this.sectionName = this.dataset.sectionId || "universal-products";
    this.collectionUrl = (this.dataset.collectionUrl || window.location.pathname).replace(/\/$/, "");
    this.tagParam = this.dataset.tagParam || "filter.p.tag";
    this.tagValue = this.dataset.tagValue || "";
    this.pageSize = parseInt(this.dataset.pageSize, 10) || 24;
    this.vehicleCount = parseInt(this.dataset.vehicleCount, 10) || 0;
    this.maxPages = 30; // safety cap on universal-stream pagination
    this._busy = false;
    this._queued = false;
    this._cacheKey = null;
    this._cache = null;
  }

  connectedCallback() {
    this.container = document.getElementById("ProductGridContainer");
    if (!this.container) return;
    this.augment();
  }

  currentParams() {
    return new URLSearchParams(window.location.search);
  }

  nonVehicleParams(params) {
    const out = new URLSearchParams();
    for (const [key, value] of params.entries()) {
      if (key.includes("vehicle_")) continue;
      if (key === "page") continue;
      out.append(key, value);
    }
    return out;
  }

  mainSectionId() {
    const grid = document.getElementById("product-grid") || document.getElementById("product-rows");
    return grid ? grid.dataset.id : null;
  }

  async fetchUniversalPage(baseParams, availability, page) {
    const qs = new URLSearchParams(baseParams.toString());
    qs.set("sections", this.sectionName);
    qs.append(this.tagParam, this.tagValue); // filter.p.tag=Universal (group OR's the tags)
    qs.set("filter.v.availability", availability); // "1" in-stock, "0" out-of-stock
    qs.set("page", String(page));
    const url = `${this.collectionUrl}?${qs.toString()}`;
    // Retry once so a single slow/failed request doesn't truncate the set.
    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        const response = await fetch(url);
        if (!response.ok) continue;
        const json = await response.json();
        const html = json[this.sectionName];
        if (!html) return null;
        const doc = new DOMParser().parseFromString(html, "text/html");
        return doc.querySelector("[data-universal-products]");
      } catch (error) {
        /* retry */
      }
    }
    return null;
  }

  // Fetch every page of one availability slice. Page 1 first (to learn the page
  // count), then the rest in parallel — ~2 round-trips regardless of size.
  async fetchAllUniversal(baseParams, availability) {
    const first = await this.fetchUniversalPage(baseParams, availability, 1);
    if (!first) return [];
    const pages = Math.min(parseInt(first.dataset.pages, 10) || 1, this.maxPages);
    const rest = await Promise.all(
      Array.from({ length: Math.max(0, pages - 1) }, (unused, k) =>
        this.fetchUniversalPage(baseParams, availability, k + 2)
      )
    );
    const items = [];
    for (const block of [first, ...rest]) {
      if (!block) continue;
      const list = block.querySelector("[data-universal-list]");
      if (!list) continue;
      items.push(...list.children);
    }
    return items;
  }

  async getUniversal(params) {
    const baseParams = this.nonVehicleParams(params);
    const key = baseParams.toString();
    if (this._cacheKey === key && this._cache) return this._cache;

    // In-stock first, then out-of-stock. Both slices fetched concurrently.
    const [inStock, outOfStock] = await Promise.all([
      this.fetchAllUniversal(baseParams, "1"),
      this.fetchAllUniversal(baseParams, "0"),
    ]);

    // Concatenate (in-stock first) and de-duplicate by handle, defensively.
    const items = [];
    const seen = new Set();
    for (const li of [...inStock, ...outOfStock]) {
      const handle = li.getAttribute("data-universal-handle");
      if (handle && !seen.has(handle)) {
        seen.add(handle);
        items.push(li);
      }
    }

    this._cacheKey = key;
    this._cache = items;
    return items;
  }

  async fetchVehiclePage(params, page) {
    const sectionId = this.mainSectionId();
    if (!sectionId) return [];
    const qs = new URLSearchParams(params.toString());
    qs.set("section_id", sectionId);
    qs.set("page", String(page));
    const url = `${this.collectionUrl}?${qs.toString()}`;
    const response = await fetch(url);
    if (!response.ok) return [];
    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, "text/html");
    const grid = doc.getElementById("product-grid") || doc.getElementById("product-rows");
    return grid ? [...grid.children] : [];
  }

  // ---- orchestration ----------------------------------------------------

  async augment() {
    if (this._busy) {
      this._queued = true;
      return;
    }
    this._busy = true;
    try {
      const params = this.currentParams();
      const page = parseInt(params.get("page"), 10) || 1;

      // Hide the count until the combined (vehicle + universal) total is known,
      // so the vehicle-only number isn't shown first. Reveal happens in
      // renderCount() (combined) or below (no universal). JS-only — search pages,
      // which never instantiate this controller, are unaffected.
      const countElement = document.getElementById("ProductCount");
      if (countElement) countElement.style.visibility = "hidden";

      // Fetch the universal set and this page's vehicle items concurrently.
      const needVehicle = (page - 1) * this.pageSize < this.vehicleCount;
      const [universal, vehicleItems] = await Promise.all([
        this.getUniversal(params),
        needVehicle ? this.fetchVehiclePage(params, page) : Promise.resolve([]),
      ]);
      const universalCount = universal.length;
      const combined = this.vehicleCount + universalCount;

      // Nothing meaningful to sort when the combined total is 0 or 1 → hide the
      // sort options the server forced visible (matches the theme, which hides
      // sort for <= 1 product).
      if (combined <= 1) this.hideSort();

      if (universalCount === 0) {
        // No universal products → the server (vehicle-only) count is correct.
        // "visible" (not "") so it overrides the server-side is-universal-pending class.
        if (countElement) countElement.style.visibility = "visible";
        // Combined total is 0 (no vehicle matches and no universal) → reveal the
        // "No products found" message that shipped hidden in the empty branch.
        if (combined === 0) {
          this.container.querySelector("[data-universal-empty]")?.removeAttribute("hidden");
        }
        return; // leave the native grid alone
      }

      const totalPages = Math.max(1, Math.ceil(combined / this.pageSize));

      // Universal slice for this page (universal items follow the vehicle items).
      const uniStart = Math.max((page - 1) * this.pageSize, this.vehicleCount);
      const uniEnd = Math.min(page * this.pageSize, combined);
      const uniSlice =
        uniEnd > uniStart ? universal.slice(uniStart - this.vehicleCount, uniEnd - this.vehicleCount) : [];

      this.commit(vehicleItems, uniSlice, combined, page, totalPages);
    } finally {
      this._busy = false;
      if (this._queued) {
        this._queued = false;
        this.augment();
      }
    }
  }

  commit(vehicleItems, uniSlice, combined, page, totalPages) {
    const grid = document.getElementById("product-grid") || document.getElementById("product-rows");
    if (grid) {
      const nodes = [
        ...vehicleItems.map((n) => n.cloneNode(true)),
        ...uniSlice.map((n) => n.cloneNode(true)),
      ];
      grid.replaceChildren(...nodes);
    }

    this.renderCount(combined);
    this.renderPagination(page, totalPages);
    this.reinit();
  }

  renderCount(combined) {
    const el = document.getElementById("ProductCount");
    if (!el) return;
    let text = el.textContent.trim();
    if (/[\d,]+/.test(text)) {
      text = text.replace(/[\d,]+/, combined.toLocaleString());
      // Keep the noun's pluralisation in step with the combined total.
      if (combined === 1) {
        text = text.replace(/\bProducts\b/i, "Product");
      } else {
        text = text.replace(/\bProduct\b(?!s)/i, "$&s");
      }
    } else {
      text = combined.toLocaleString();
    }
    el.textContent = text;
    el.classList.remove("loading");
    // "visible" (not "") so it overrides the server-side is-universal-pending class.
    el.style.visibility = "visible";
  }

  pageWindow(current, total) {
    const pages = new Set([1, total, current, current - 1, current + 1]);
    const sorted = [...pages].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
    const out = [];
    let prev = 0;
    for (const p of sorted) {
      if (p - prev > 1) out.push("…");
      out.push(p);
      prev = p;
    }
    return out;
  }

  hrefForPage(page) {
    const q = new URLSearchParams(window.location.search);
    q.set("page", String(page));
    return `${window.location.pathname}?${q.toString()}`;
  }

  renderPagination(current, total) {
    this.container.querySelectorAll("[data-universal-pagination]").forEach((n) => n.remove());
    // Remove the theme's native pagination while we own paging.
    this.container.querySelectorAll(".pagination:not([data-universal-pagination])").forEach((n) => {
      const wrap = n.closest("ajax-paginate");
      (wrap || n).remove();
    });
    if (total <= 1) return;

    // Mirror the theme's snippets/pagination.liquid markup exactly (anchors +
    // page-number classes + arrow SVGs) so component-pagination.css styles it.
    const arrowLeft =
      '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="icon icon-chevron-left"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M15 6l-6 6l6 6" /></svg>';
    const arrowRight =
      '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="icon icon-chevron-right"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M9 6l6 6l-6 6" /></svg>';

    let html = "";
    if (current > 1) {
      html += `<a href="${this.hrefForPage(current - 1)}" class="page-number page-number-prev" data-uni-page="${current - 1}" aria-label="Previous page">${arrowLeft}</a>`;
    }
    for (const p of this.pageWindow(current, total)) {
      if (p === "…") {
        html += '<span class="page-number">…</span>';
      } else if (p === current) {
        html += `<span class="page-number page-number-active">${p}</span>`;
      } else {
        html += `<a href="${this.hrefForPage(p)}" class="page-number" data-uni-page="${p}" aria-label="Page ${p}">${p}</a>`;
      }
    }
    if (current < total) {
      html += `<a href="${this.hrefForPage(current + 1)}" class="page-number page-number-next" data-uni-page="${current + 1}" aria-label="Next page">${arrowRight}</a>`;
    }

    const wrap = document.createElement("div");
    wrap.className = "pagination";
    wrap.setAttribute("data-universal-pagination", "");
    wrap.setAttribute("data-page", String(current));
    wrap.innerHTML = html;
    wrap.querySelectorAll("a[data-uni-page]").forEach((a) => {
      a.addEventListener("click", (event) => {
        event.preventDefault();
        this.goToPage(parseInt(a.dataset.uniPage, 10));
      });
    });
    this.container.appendChild(wrap);
  }

  goToPage(page) {
    const params = this.currentParams();
    params.set("page", String(page));
    const searchParams = params.toString();
    // Match the theme's history-state shape so its own popstate handler stays happy.
    window.history.pushState({ searchParams }, "", `${window.location.pathname}?${searchParams}`);
    document.getElementById("MainContent")?.scrollIntoView(true);
    this.augment();
  }

  // Hide the sort label + dropdown when there is nothing to sort. The theme
  // re-renders this bar fresh on every filter change, so it reappears for any
  // filter set that has results — no explicit un-hide needed.
  hideSort() {
    document.querySelector('label[for="collection-sort-order"]')?.setAttribute("hidden", "");
    document.querySelector(".collection-sort-wrapper")?.setAttribute("hidden", "");
  }

  reinit() {
    if (window.yotpoWidgetsContainer && typeof window.yotpoWidgetsContainer.initWidgets === "function") {
      window.yotpoWidgetsContainer.initWidgets();
    } else if (window.yotpo && typeof window.yotpo.refreshWidgets === "function") {
      window.yotpo.refreshWidgets();
    }
    try {
      gwCollectionPageRender();
    } catch (error) {
      /* analytics helper not present on this page */
    }
  }
}

customElements.define("universal-products", UniversalProducts);
